import { useId, type FC } from 'react';
import { type ArrayElement } from 'dibs-ts-utils/exports/ArrayElement.ts';

import { useFragment, graphql } from 'react-relay';
import { MEASUREMENT_UNIT, WIDTH, HEIGHT } from '../sbSharedRefineMenuConstants.ts';
import { useIntl, defineMessages, type IntlShape } from 'dibs-react-intl';
import { useMeasurementUnit } from '../../useMeasurementUnit.ts';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls.ts';
import { getAbbreviations } from '../../../constants/measurementUnits.ts';
import { IN, CM, type MeasurementUnit } from 'dibs-intl/exports/units';

import { SbSharedRefineMenuMultiSelectList } from '../SbSharedRefineMenuMultiSelect/SbSharedRefineMenuMultiSelectList.jsx';
import { SbSharedRefineMenuDimensionUnitRadioSelect } from '../SbSharedRefineMenuDimensionUnitRadioSelect/SbSharedRefineMenuDimensionUnitRadioSelect.tsx';
import { SbSharedRefineMenuDimensionFacet } from '../SbSharedRefineMenuDimensionFacet/SbSharedRefineMenuDimensionFacet.jsx';
import { SbSharedRefineMenuInputRange } from '../SbSharedRefineMenuInputRange/SbSharedRefineMenuInputRange.tsx';

import { type SbSharedRefineMenuFilterArtSize_itemSearch$key } from './__generated__/SbSharedRefineMenuFilterArtSize_itemSearch.graphql.ts';
import { type SbSharedRefineMenuFilterArtSize_user$key } from './__generated__/SbSharedRefineMenuFilterArtSize_user.graphql.ts';
import { type SbSharedRefineMenuFilterArtSize_filters$key } from './__generated__/SbSharedRefineMenuFilterArtSize_filters.graphql.ts';
import { type SbSharedRefineMenuFilters_filters$data } from './__generated__/SbSharedRefineMenuFilters_filters.graphql.ts';

type ArtSizeValues = ArrayElement<SbSharedRefineMenuFilters_filters$data>['values'];

const messages = defineMessages({
    artSizeRange: {
        id: 'sb.SbSharedRefineMenuFilterArtSize.range',
        defaultMessage: '{displayName} ({formattedMin}—{formattedMax})',
    },
    artSizeRangeMin: {
        id: 'sb.SbSharedRefineMenuFilterArtSize.rangeMin',
        defaultMessage: '{displayName} ({formattedMin}+)',
    },
    artSizeRangeMax: {
        id: 'sb.SbSharedRefineMenuFilterArtSize.rangeMax',
        defaultMessage: '{displayName} (<{formattedMax})',
    },
    overallWidthTitle: {
        id: 'sb.SbSharedRefineMenuFilterArtSize.overallWidthTitle',
        defaultMessage: 'Overall Width',
    },
    overallHeightTitle: {
        id: 'sb.SbSharedRefineMenuFilterArtSize.overallHeightTitle',
        defaultMessage: 'Overall Height',
    },
    dimensionFromLabel: {
        id: 'sb.SbSharedRefineMenuFilterArtSize.dimensionFromLabel',
        defaultMessage: '{measurementUnit} min',
    },
    dimensionToLabel: {
        id: 'sb.SbSharedRefineMenuFilterArtSize.dimensionToLabel',
        defaultMessage: '{measurementUnit} max',
    },
});

type FormatMeasurementArgs = {
    intl: IntlShape;
    value: string | null;
    measurementUnit: MeasurementUnit;
};

const formatMeasurement = ({
    intl,
    value,
    measurementUnit,
}: FormatMeasurementArgs): string | null => {
    if (value === null || value === '0') {
        return null;
    }
    const numericValue = Number(value);

    const values: Record<MeasurementUnit, number> = {
        [IN]: Math.round(numericValue * 10) / 10,
        [CM]: Math.round(numericValue * 2.54 * 10) / 10, // 1 inch = 2.54 cm
    };

    const units: Record<MeasurementUnit, string> = {
        [IN]: 'inch',
        [CM]: 'centimeter',
    };

    return intl.formatNumber(values[measurementUnit], {
        style: 'unit',
        unit: units[measurementUnit],
    });
};

type FormatDisplayNameArgs = {
    intl: IntlShape;
    artSizeValue: ArrayElement<ArtSizeValues>;
    measurementUnit: MeasurementUnit;
};

export const formatDisplayName = ({
    intl,
    artSizeValue,
    measurementUnit,
}: FormatDisplayNameArgs): string => {
    const { displayName, properties } = artSizeValue || {};

    const formattedMin = formatMeasurement({
        intl,
        value: properties?.min || null,
        measurementUnit,
    });
    const formattedMax = formatMeasurement({
        intl,
        value: properties?.max || null,
        measurementUnit,
    });

    if (formattedMax === null) {
        return intl.formatMessage(messages.artSizeRangeMin, { displayName, formattedMin });
    }

    if (formattedMin === null) {
        return intl.formatMessage(messages.artSizeRangeMax, { displayName, formattedMax });
    }

    return intl.formatMessage(messages.artSizeRange, { displayName, formattedMin, formattedMax });
};

type Props = {
    itemSearch: SbSharedRefineMenuFilterArtSize_itemSearch$key;
    filters: SbSharedRefineMenuFilterArtSize_filters$key;
    user: SbSharedRefineMenuFilterArtSize_user$key;
    filterName: string;
    shouldUseExpandableList: boolean;
    values: ArtSizeValues;
    applyOnBlur?: boolean;
    showInvalidRangeErrorMessage?: boolean;
    size?: 'small' | 'medium' | 'large';
    trackingOptions?: Record<string, unknown>;
};

export const SbSharedRefineMenuFilterArtSize: FC<Props> = ({
    user: userRef,
    itemSearch: itemSearchRef,
    filters: filtersRef,
    filterName,
    values,
    shouldUseExpandableList,
    applyOnBlur,
    showInvalidRangeErrorMessage,
    size,
    trackingOptions,
}) => {
    const itemSearch = useFragment(
        graphql`
            fragment SbSharedRefineMenuFilterArtSize_itemSearch on ItemSearchQueryConnection {
                ...SbSharedRefineMenuMultiSelectList_itemSearch
                ...SbSharedRefineMenuInputRange_itemSearch
                ...useMeasurementUnit_itemSearch
                ...SbSharedRefineMenuDimensionUnitRadioSelect_itemSearch
            }
        `,
        itemSearchRef
    );

    const user = useFragment(
        graphql`
            fragment SbSharedRefineMenuFilterArtSize_user on User {
                ...useMeasurementUnit_user
                ...SbSharedRefineMenuDimensionUnitRadioSelect_user
            }
        `,
        userRef
    );

    const filters = useFragment(
        graphql`
            fragment SbSharedRefineMenuFilterArtSize_filters on SearchBrowseFilter
            @relay(plural: true) {
                name
                values {
                    ...SbSharedRefineMenuInputRange_filterValue
                }
            }
        `,
        filtersRef
    );

    const intl = useIntl();
    const htmlIdBase = useId();
    const [measurementUnit] = useMeasurementUnit({ user, itemSearch });

    const displayedArtSizeValues = (values || []).filter(filterNulls).map(artSizeValue => ({
        ...artSizeValue,
        displayName: formatDisplayName({
            intl,
            artSizeValue,
            measurementUnit,
        }),
    }));

    const dimensionFilters = (filters || []).filter(f => f.name === WIDTH || f.name === HEIGHT);
    const getLocalizedName = (name: string | null): string => {
        const message = name === WIDTH ? messages.overallWidthTitle : messages.overallHeightTitle;
        return intl.formatMessage(message);
    };

    return (
        <>
            <SbSharedRefineMenuDimensionUnitRadioSelect
                user={user}
                itemSearch={itemSearch}
                selectedMeasurementUnit={measurementUnit}
                trackingOptions={trackingOptions}
            />

            <div>
                <SbSharedRefineMenuMultiSelectList
                    filterName={filterName}
                    values={displayedArtSizeValues}
                    shouldUseExpandableList={shouldUseExpandableList}
                    itemSearch={itemSearch}
                    trackingOptions={trackingOptions}
                />
            </div>
            {(dimensionFilters || []).map(dimensionFilter => {
                const { name, values: filterValues } = dimensionFilter;
                const id = `${htmlIdBase}-${name}`;
                const localizedFilterName = getLocalizedName(name);
                const localizedMeasurementUnit = getAbbreviations({
                    intl,
                    measurementUnit,
                });

                return (
                    <SbSharedRefineMenuDimensionFacet
                        id={id}
                        key={name}
                        title={localizedFilterName}
                    >
                        <SbSharedRefineMenuInputRange
                            labelFrom={intl.formatMessage(messages.dimensionFromLabel, {
                                measurementUnit: localizedMeasurementUnit,
                            })}
                            labelTo={intl.formatMessage(messages.dimensionToLabel, {
                                measurementUnit: localizedMeasurementUnit,
                            })}
                            ariaDescribedBy={id}
                            size={size}
                            filterName={name}
                            filterValue={filterValues?.[0]}
                            applyOnBlur={applyOnBlur}
                            statKey={measurementUnit}
                            additionalFilter={MEASUREMENT_UNIT}
                            itemSearch={itemSearch}
                            showInvalidRangeErrorMessage={showInvalidRangeErrorMessage}
                            trackingOptions={trackingOptions}
                        />
                    </SbSharedRefineMenuDimensionFacet>
                );
            })}
        </>
    );
};
